import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { ArrowSimple } from '@stokr/components-library'
import { Chevron } from 'components/ui/collapsible/Collapsible.styles'
import { SectionSkeleton } from './SectionSkeleton'
import { findAnchor, type StoSectionId } from 'constants/stoAnchors'
import { getAnchorIdFromHash } from 'hooks/sto/useStoAnchors'
import { useIsBelowDesktop } from 'hooks/sto/useIsBelowDesktop'
import { useSectionViewTracking, useStoTrack } from 'hooks/sto/useStoTracking'
import type { SectionState } from 'models/stoSection'
import {
  SectionBody,
  SectionBodyClip,
  SectionBodyInner,
  SectionHeader,
  SectionHeading,
  SectionRoot,
  SectionToggle,
} from './Section.styles'

interface SectionProps<T> {
  /** Doubles as the DOM id and the anchor target. */
  anchor: StoSectionId
  title?: ReactNode
  state: SectionState<T>
  children: (data: T) => ReactNode
  skeletonRows?: number
  /** `plain` sits on the page background instead of in a card. */
  variant?: 'card' | 'plain'
  /** Figma elevation/xs on the card. */
  elevated?: boolean
  /**
   * The card collapses to its title at every width: `'always'` starts closed, `'desktop-open'` starts
   * closed below desktop and open from desktop up.
   */
  collapsible?: 'always' | 'desktop-open'
  /** A second copy of a section rendered elsewhere for another breakpoint: no DOM id. */
  mirror?: boolean
}

/** Opens when the hash names this section or anything nested in it. */
const useOpenOnHash = (anchor: StoSectionId, open: () => void) => {
  const { hash } = useLocation()
  useEffect(() => {
    const id = getAnchorIdFromHash(hash)
    if (id && findAnchor(id)?.section === anchor) open()
  }, [hash, anchor, open])
}

/**
 * Single implementation of the visibility rule: hidden renders nothing, loading
 * renders a skeleton, ready renders children with narrowed data. Sections cannot
 * forget the rule because `state` is the only way in.
 */
export function Section<T>({
  anchor,
  title,
  state,
  children,
  skeletonRows,
  variant = 'card',
  elevated = false,
  collapsible: collapsibleProp,
  mirror = false,
}: SectionProps<T>) {
  // Without a title there is nothing to tap, so the section never collapses.
  const collapsible = Boolean(collapsibleProp) && Boolean(title)
  const desktopOpen = collapsibleProp === 'desktop-open'
  const [open, setOpen] = useState(false)
  // `desktop-open` follows the width (CSS, so the server render is right) until the first toggle.
  const [touched, setTouched] = useState(false)
  const isMobile = useIsBelowDesktop()
  const followsWidth = desktopOpen && !touched
  const isOpen = followsWidth ? !isMobile : open
  // Once toggled (or `always`), the explicit state drives the body, not the breakpoint.
  const explicit = !followsWidth
  const bodyId = useId()
  const openSection = useCallback(() => {
    setOpen(true)
    setTouched(true)
  }, [])
  useOpenOnHash(anchor, openSection)
  const rootRef = useRef<HTMLElement>(null)
  // A mirror (the copy for another width) reports too: whichever is on screen, once per page view.
  useSectionViewTracking(rootRef, anchor, state.status === 'ready')
  const trackSto = useStoTrack()

  if (state.status === 'hidden') return null

  const content = state.status === 'loading' ? <SectionSkeleton rows={skeletonRows} /> : children(state.data)
  const collapsed = collapsible && (followsWidth || !isOpen)

  return (
    <SectionRoot
      ref={rootRef}
      id={mirror ? undefined : anchor}
      data-sto-section={anchor}
      $variant={variant}
      $elevated={elevated}
      $collapsible={collapsible}
      $collapsed={collapsed}
      $always={explicit}>
      {title && (
        <SectionHeader $collapsed={collapsed} $always={explicit}>
          {collapsible ? (
            <SectionHeading>
              <SectionToggle
                type="button"
                $collapsed={collapsed}
                $always={explicit}
                aria-expanded={isOpen}
                aria-controls={bodyId}
                data-cy={`sto-section-toggle-${anchor}`}
                onClick={() => {
                  trackSto('sto_section_toggled', { section: anchor, open: !isOpen })
                  setOpen(!isOpen)
                  setTouched(true)
                }}>
                {title}
                <Chevron $open={isOpen} aria-hidden="true">
                  <ArrowSimple width={10} height={6} />
                </Chevron>
              </SectionToggle>
            </SectionHeading>
          ) : (
            <SectionHeading>{title}</SectionHeading>
          )}
        </SectionHeader>
      )}
      {collapsible ? (
        <SectionBody id={bodyId} $open={!followsWidth && open} $always={explicit} inert={!isOpen}>
          <SectionBodyClip $always={explicit}>
            <SectionBodyInner>{content}</SectionBodyInner>
          </SectionBodyClip>
        </SectionBody>
      ) : (
        content
      )}
    </SectionRoot>
  )
}
